import type { NavNode } from '@trinetra/shared';
import { SITE_NAV } from './navigation';

/**
 * Which cloud routes need the selected site's TUNNEL — i.e. every page whose data is fetched
 * through that node and which is therefore meaningless when it is offline. The per-site menu is
 * most of that list, so it is derived from SITE_NAV — a menu entry added there is covered here
 * for free — plus the tunnelled routes that were never in the nav.
 *
 * Its caller is the Layout's offline guard, which blocks the page when the site is unreachable.
 * (The old NodeScopeTitle banner shared this list; the title bar names the site now.)
 *
 * Not the same question as `isAllSitesPath` in constants/scope.ts. That one asks which SCOPE a
 * page belongs to; this one asks whether it needs the tunnel. Site Health belongs to a site and
 * does not need the tunnel, so the two answers differ for it — which is the point.
 */

/** Recursively collect the `to` paths of a nav tree. */
function navPaths(items: NavNode[]): string[] {
  return items.flatMap((item) => [...(item.to ? [item.to] : []), ...navPaths(item.children || [])]);
}

/**
 * Scoped to a site, but served by the cloud rather than through the node — so they keep working
 * while the site is down and the offline guard must not block them. Site Health is the whole
 * reason: it reads the cloud's own monitoring history, and it is the one page you still want
 * when a site stops answering.
 */
const SURVIVES_OFFLINE = ['/site-health', '/node-monitor/'];

const survivesOffline = (pathname: string) =>
  SURVIVES_OFFLINE.some((p) => pathname === p || pathname.startsWith(p));

/** Entries ending in "/" match as a bare prefix. */
const NODE_SCOPED_PATHS = [
  ...navPaths(SITE_NAV).filter((p) => !survivesOffline(p)),
  // The alert board's other URL. Its nav entry points at /hexagon-alerts and carries
  // /map-view as `alsoActiveFor`, which navPaths doesn't see — but both paths render the
  // same node-scoped board, so the offline guard has to cover them both.
  '/map-view',
  '/nodes/', // camera detail, ONVIF config and per-camera heatmap
  '/camera-management',
  '/alert-config',
  // Parents of the nav's own entries: both have slugs the nav hides (the reports for models
  // nobody runs), and those pages are just as node-scoped as the listed ones.
  '/reports',
  '/attendance',
  // One event as a page (/events/:model/:id) — reached from a report's detail modal or a pasted
  // link, and every bit as node-scoped as the report it came from.
  '/events',
];

export function isNodeScoped(pathname: string): boolean {
  if (survivesOffline(pathname)) return false;
  return NODE_SCOPED_PATHS.some((p) =>
    p.endsWith('/') ? pathname.startsWith(p) : pathname === p || pathname.startsWith(`${p}/`),
  );
}
